import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, ShoppingCart, BookOpen, Home } from 'lucide-react';
// Try the alternative import syntax for SVG
import AmazonSvgIcon from '@/assets/amazon-icon-logo-logo-brandlogos.net_84hrve1sk.svg';
// If you have Firebase functions set up, import them here
// import { getFunctions, httpsCallable } from 'firebase/functions';

// Define a type for the mock transactions
interface MockTransaction {
  id: string;
  date: string;
  amount: number;
  currency: string;
  description: string;
  category?: string; // Optional category like in the mock response
  icon?: React.ReactNode; // Optional icon - Changed to ReactNode to accept image src
}

const exampleQuestions = [
  "List my recent transactions over $100.",
  "Did I have any 'Travel' expenses last month?",
  "Show transactions from 'Amazon' in the last 90 days.",
  "What was my largest deposit this year?"
];

export function AIChatCard() {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState<MockTransaction[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const functions = getFunctions(); // Initialize Firebase Functions if needed

  const handleAskAI = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setError(null);
    setResponse(null);

    console.log("Asking AI (placeholder):", question);
    try {
      // Check for specific demo query about Amazon
      if (question.toLowerCase().includes('amazon')) {
        // Simulate a slightly faster response for the demo
        await new Promise(resolve => setTimeout(resolve, 500));
        // Create mock transaction objects
        const mockAmazonTransactions: MockTransaction[] = [
          {
            id: 'mock-amazon-1',
            date: 'March 15, 2024',
            amount: 45.99,
            currency: 'USD',
            description: 'Amazon Marketplace',
            category: 'Electronics',
            // Use the imported SVG path as the src for an img tag
            icon: <img src={AmazonSvgIcon} alt="Amazon" className="h-4 w-4" /> 
          },
          {
            id: 'mock-amazon-2',
            date: 'March 10, 2024',
            amount: 12.50,
            currency: 'USD',
            description: 'Amazon Books',
            category: 'Books',
            icon: <img src={AmazonSvgIcon} alt="Amazon" className="h-4 w-4" /> 
          },
          {
            id: 'mock-amazon-3',
            date: 'February 28, 2024',
            amount: 88.10,
            currency: 'USD',
            description: 'Amazon Retail',
            category: 'Home Goods',
            icon: <img src={AmazonSvgIcon} alt="Amazon" className="h-4 w-4" /> 
          },
        ];
        setResponse(mockAmazonTransactions); // Set the array
      } else {
        // --- Original Placeholder for other questions (returns string, not cards) ---
        // ... (simulate delay)
        await new Promise(resolve => setTimeout(resolve, 1500));
        const aiResponse = `Placeholder response for: \"${question}\". AI integration coming soon!`;
        // For non-Amazon queries, we could set a single mock item with the text,
        // but for clarity, let's keep it simple and not render cards for placeholders.
        // If we wanted to show the placeholder string *as if* it were a transaction card:
        // setResponse([{ id: 'placeholder', date: '', amount: 0, currency: '', description: aiResponse }]);
        // However, this might be confusing. Resetting to null is clearer.
        setResponse(null);
        // Display the placeholder text in a separate area if needed, or just log it.
        console.log("Placeholder response:", aiResponse); 
        // Or potentially set a different state variable for text responses
        // setTextResponse(aiResponse);
      }
    } catch (err: any) {
      console.error("Error calling AI function:", err);
      setError("Sorry, I couldn't get an answer right now. Please try again later.");
      setResponse(null); // Clear response on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isLoading) {
      handleAskAI();
    }
  };

  const handleExampleClick = (example: string) => {
    setQuestion(example);
    // Optionally trigger the AI call immediately
    // handleAskAI(); 
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Ask AI About Your Finances
        </CardTitle>
        <CardDescription>Get insights on your spending, transactions, and more.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="e.g., How much did I spend on groceries last month?"
            value={question}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <Button onClick={handleAskAI} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Asking...
              </>
            ) : (
              'Ask'
            )}
          </Button>
        </div>

        {/* Example Questions Area */}
        {!isLoading && !response && !error && (
          <div className="space-y-2 pt-2">
            <p className="text-xs text-muted-foreground">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQuestions.map((ex, index) => (
                <Button 
                  key={index} 
                  variant="outline" 
                  size="sm"
                  className="h-auto text-xs py-1 px-2"
                  onClick={() => handleExampleClick(ex)}
                >
                  {ex}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Response Area: Render cards if response is an array */}
        {response && Array.isArray(response) && response.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium mb-2">AI Response:</p>
            {response.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                   <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center border overflow-hidden">
                       {tx.icon || <Sparkles className="h-4 w-4 text-muted-foreground" />}
                   </div>
                   <div className="flex flex-col min-w-0">
                     <span className="font-medium text-sm truncate text-foreground">{tx.description}</span>
                     <span className="text-xs text-muted-foreground truncate">
                       {tx.date} {tx.category ? `• ${tx.category}` : ''}
                     </span>
                   </div>
                </div>
                <div className="flex items-center gap-2">
                   <span className="text-xs sm:text-sm font-medium text-foreground">
                     {new Intl.NumberFormat('en-US', { style: 'currency', currency: tx.currency || 'USD' }).format(tx.amount)}
                   </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 border border-destructive/50 bg-destructive/10 rounded-lg text-sm text-destructive">
            <p>{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 